import { useContext, useEffect, useCallback, useState, useReducer } from 'react';
import { ToastContext } from 'contexts';
import axios from 'helpers/axios';
import { PROJECTS_SEARCH } from 'constants/apis';
import { SORT_BY, PROJECT_STATUS } from 'constants/opportunities';

const getDirection = (statuses, sort) =>
  sort === SORT_BY.closingSoon &&
  (statuses === PROJECT_STATUS.open || statuses === PROJECT_STATUS.allOpportunities)
    ? 'asc'
    : 'desc';

const initialLoadingState = {
  shouldAppend: false,
  page: 1,
  updateQueued: false,
  isLoading: false,
};
const loadingReducer = (state, action) => {
  switch (action.type) {
    case 'nextPage':
      if (state.isLoading || state.updateQueued) {
        return state;
      }
      return { ...state, page: state.page + 1, shouldAppend: true, updateQueued: true };
    case 'triggerSearch':
      return { ...state, page: 1, shouldAppend: false, updateQueued: true };
    case 'startLoad':
      return { ...state, updateQueued: false, isLoading: true };
    case 'endLoad':
      return { ...state, isLoading: false };
    default:
      throw new Error();
  }
};
const useSearchTab = (
  type,
  { search, sortBy, locations, organizations, statuses, sources, types }
) => {
  const { triggerToast } = useContext(ToastContext);
  const [fetchingState, dispatchFetchUpdate] = useReducer(loadingReducer, initialLoadingState);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(null);

  const fetchMore = useCallback(() => {
    if (data.length < total) {
      dispatchFetchUpdate({ type: 'nextPage' });
    }
  }, [data.length, total]);

  const triggerSearch = useCallback(() => {
    setData([]);
    dispatchFetchUpdate({ type: 'triggerSearch' });
  }, []);

  const fetchOpportunitiesSearch = useCallback(
    ({
      locations,
      statuses,
      sources,
      organizations,
      page,
      search,
      shouldAppend,
      sortBy,
      types,
    }) => {
      const requestData = {
        type: type === 'following' ? 'pinned' : type,
        statuses: statuses.split(','),
        sources: sources.split(','),
        projectTypes: types,
        search,
      };

      if (locations.length) requestData.locations = locations;
      if (organizations.length) {
        requestData.organizationUuids = organizations
          .filter(({ OrganizationUUID }) => !!OrganizationUUID)
          .map(({ OrganizationUUID }) => OrganizationUUID);
        requestData.organizationNames = organizations
          .filter(({ OrganizationUUID }) => !OrganizationUUID)
          .map(({ OrganizationName }) => OrganizationName);
      }

      dispatchFetchUpdate({ type: 'startLoad' });

      const options = {
        params: {
          limit: 10,
          page,
          sort: sortBy,
          direction: getDirection(statuses, sortBy),
        },
      };

      axios
        .post(PROJECTS_SEARCH, requestData, options)
        .then(resp => {
          const results = resp.data.map(project => ({
            ...project,
          }));
          if (shouldAppend) {
            setData(prevProjects => [...prevProjects, ...results]);
          } else {
            setData(results);
          }
          setTotal(parseInt(resp.headers['x-total-count'], 10));
          dispatchFetchUpdate({ type: 'endLoad' });
        })
        .catch(() => {
          triggerToast({});
          dispatchFetchUpdate({ type: 'endLoad' });
        });
    },
    [triggerToast, type]
  );

  useEffect(() => {
    if (fetchingState.updateQueued && !fetchingState.isLoading) {
      fetchOpportunitiesSearch({
        locations,
        organizations,
        page: fetchingState.page,
        search,
        shouldAppend: fetchingState.shouldAppend,
        sortBy,
        sources,
        statuses,
        types,
      });
    }
  }, [
    fetchOpportunitiesSearch,
    fetchingState.isLoading,
    fetchingState.page,
    fetchingState.shouldAppend,
    fetchingState.updateQueued,
    locations,
    organizations,
    search,
    sortBy,
    sources,
    statuses,
    types,
  ]);

  const updateFollowing = useCallback(
    (projectUUID, followed) => {
      const setProjectFollowingStatus = data => {
        const idx = data.findIndex(project => project.ProjectUUID === projectUUID);
        if (idx === -1) {
          return data;
        }
        const modifiedProjects = data.slice();
        modifiedProjects[idx].IsFollowing = followed;
        return modifiedProjects;
      };
      if (type === 'following') {
        setTotal(prevTotal => (followed ? prevTotal + 1 : prevTotal - 1));
      }
      setData(data => setProjectFollowingStatus(data));
    },
    [type]
  );
  return {
    total,
    data,
    isLoading: fetchingState.isLoading,
    shouldAppend: fetchingState.shouldAppend,
    updateFollowing,
    fetchMore,
    triggerSearch,
  };
};

export default useSearchTab;
